import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { Box, GKButton, GKClientDataGrid, Grid2, Typography } from "components";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { moneyCommaSeparator } from "utils";
import { extraColumnsField } from "utils/helpers";
import type { FolioWithHolding } from "./EnterAmountFolioTable";
import FilterPopover from "./FilterPopover";
import type { MFOrderFormValues, MFOrderItem } from "./useMFOrders";
import { getInitialMFOrderItem } from "./useMFOrders";

export type MFReadinessStatus = "mf_ready" | "pending" | "unavailable";

const STATUS_STYLE: Record<
  MFReadinessStatus,
  { bg: string; color: string; label: string }
> = {
  mf_ready: { bg: "#e8f3ec", color: "#15803d", label: "MF Ready" },
  pending: { bg: "#f9f2dd", color: "#e6b532", label: "Pending" },
  unavailable: { bg: "#fde8e8", color: "#c35245", label: "Unavailable" },
};

export interface PortfolioRow {
  portfolioId: number;
  clientName: string;
  portfolioCode: string;
  mfReadinessStatus?: MFReadinessStatus;
  currentAmount: number;
  portfolioValue: number;
  schemeQty?: number;
  schemeFolios?: FolioWithHolding[];
}

interface Props {
  portfolios: PortfolioRow[];
  loading?: boolean;
}

const isInactiveStatus = (s?: MFReadinessStatus) =>
  s === "pending" || s === "unavailable";

const StatusPill = ({
  mfReadinessStatus,
}: {
  mfReadinessStatus?: MFReadinessStatus;
}) => {
  if (!mfReadinessStatus || !STATUS_STYLE[mfReadinessStatus]) return null;
  const { bg, color, label } = STATUS_STYLE[mfReadinessStatus];
  return (
    <Box
      sx={{
        alignItems: "center",
        bgcolor: bg,
        border: "1px solid var(--white)",
        borderRadius: "6px",
        display: "inline-flex",
        px: "12px",
        py: "6px",
      }}
    >
      <Typography
        color={color}
        fontSize={10}
        fontWeight={600}
        sx={{ letterSpacing: "0.3px", textTransform: "uppercase" }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const PortfolioSelectionTable = (props: Props) => {
  const { portfolios, loading = false } = props;

  const form = useFormContext<MFOrderFormValues>();
  const orderType = useWatch({ control: form.control, name: "orderType" });
  const isRedemption = orderType === "redemption";
  const isSwitch = orderType === "switch";
  const isHoldingsBased = isRedemption || isSwitch;

  const portfolioColumn: GridColDef<PortfolioRow> = {
    ...extraColumnsField(
      `Portfolio (${portfolios?.length || 0})`,
      "clientName",
      400,
      1,
    ),
    renderCell: (params) => {
      const isPending = isInactiveStatus(params?.row?.mfReadinessStatus);
      return (
        <Grid2
          container
          alignItems="center"
          gap={2}
          sx={{ height: "100%", width: "100%" }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              minWidth: 0,
            }}
          >
            <Typography
              color={isPending ? "var(--grey-500)" : "var(--black-900)"}
              fontSize={14}
              fontWeight={500}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params?.row?.clientName}
            </Typography>
            <Typography
              color={isPending ? "#bdbdbd" : "var(--neutral-500)"}
              fontSize={12}
              fontWeight={500}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params?.row?.portfolioCode}
            </Typography>
          </Box>
          <StatusPill mfReadinessStatus={params?.row?.mfReadinessStatus} />
        </Grid2>
      );
    },
  };

  const totalFoliosColumn: GridColDef<PortfolioRow> = {
    ...extraColumnsField("Total Folios", "schemeFolios", 120, 0),
    type: "number",
    align: "right",
    headerAlign: "right",
    renderCell: (params) => (
      <Typography
        color="var(--black-900)"
        fontSize={14}
        fontWeight={500}
        sx={{ textAlign: "right" }}
      >
        {params?.row?.schemeFolios?.length ?? 0}
      </Typography>
    ),
  };

  const schemeCurrentValueColumn: GridColDef<PortfolioRow> = {
    ...extraColumnsField("Scheme Current Value (₹)", "currentAmount", 200, 0),
    type: "number",
    align: "right",
    headerAlign: "right",
    renderCell: (params) => {
      const isPending = isInactiveStatus(params?.row?.mfReadinessStatus);
      return (
        <Typography
          color="var(--black-900)"
          fontSize={14}
          fontWeight={500}
          sx={{ opacity: isPending ? 0.5 : 1, textAlign: "right" }}
        >
          {moneyCommaSeparator(params?.row?.currentAmount ?? 0)}
        </Typography>
      );
    },
  };

  const portfolioValueColumn: GridColDef<PortfolioRow> = {
    ...extraColumnsField("Portfolio Value (₹)", "portfolioValue", 200, 0),
    type: "number",
    sortable: false,
    align: "right",
    headerAlign: "right",
    renderCell: (params) => {
      const isPending = isInactiveStatus(params?.row?.mfReadinessStatus);
      return (
        <Typography
          color="var(--black-900)"
          fontSize={14}
          fontWeight={500}
          sx={{ opacity: isPending ? 0.5 : 1, textAlign: "right" }}
        >
          {moneyCommaSeparator(params?.row?.portfolioValue ?? 0)}
        </Typography>
      );
    },
  };

  const schemeQtyColumn: GridColDef<PortfolioRow> = {
    ...extraColumnsField("Scheme Qty/Units", "schemeQty", 160, 0),
    type: "number",
    sortable: false,
    align: "right",
    headerAlign: "right",
    renderCell: (params) => {
      const qty =
        params?.row?.schemeQty ??
        (params?.row?.schemeFolios ?? []).reduce(
          (sum, folio) => sum + (folio.holdingQty ?? 0),
          0,
        );
      return (
        <Typography
          color="var(--black-900)"
          fontSize={14}
          fontWeight={500}
          sx={{ textAlign: "right" }}
        >
          {moneyCommaSeparator(qty)}
        </Typography>
      );
    },
  };

  const columns: GridColDef<PortfolioRow>[] = isHoldingsBased
    ? [
        portfolioColumn,
        totalFoliosColumn,
        schemeCurrentValueColumn,
        schemeQtyColumn,
      ]
    : [portfolioColumn, schemeCurrentValueColumn, portfolioValueColumn];

  return (
    <Controller
      name="items"
      control={form.control}
      render={({ field }) => {
        const selectedItems: MFOrderItem[] = field.value || [];
        const selectedIds = Array.from(
          new Set(
            selectedItems
              .map((item) => item.portfolioId)
              .filter((id): id is number => id != null),
          ),
        );

        const handleSelectionChange = (
          newSelectedIds: GridRowSelectionModel,
        ) => {
          const ids = newSelectedIds.map((id) => Number(id));
          const next: MFOrderItem[] = [];
          ids.forEach((id) => {
            const portfolio = portfolios.find((p) => p.portfolioId === id);
            const folios = portfolio?.schemeFolios ?? [];
            if (folios.length === 0) {
              const existing = selectedItems.find(
                (item) => item.portfolioId === id && item.folioNumber == null,
              );
              next.push(
                existing ?? { ...getInitialMFOrderItem(), portfolioId: id },
              );
            } else {
              folios.forEach((folio) => {
                const existing = selectedItems.find(
                  (item) =>
                    item.portfolioId === id &&
                    item.folioNumber === folio.folioNumber,
                );
                next.push(
                  existing ?? {
                    ...getInitialMFOrderItem(),
                    portfolioId: id,
                    folioNumber: folio.folioNumber,
                  },
                );
              });
            }
          });
          form.setValue("items", next, { shouldDirty: true });
        };

        return (
          <GKClientDataGrid
            name="mfSelectPortfolio"
            loading={loading}
            columns={columns}
            rows={portfolios || []}
            getRowId={(row) => row.portfolioId}
            isRowSelectable={(params) =>
              !isInactiveStatus(params.row?.mfReadinessStatus)
            }
            maxHeight={"calc(100vh - 415px)"}
            pagination
            columnsButton={false}
            checkboxSelection
            keepNonExistentRowsSelected
            rowSelectionModel={selectedIds}
            onRowSelectionModelChange={handleSelectionChange}
            headerComponent={
              <Typography
                color="var(--neutral-500)"
                fontSize={12}
                fontWeight={500}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "4px",
                }}
              >
                Selected Portfolios:
                <Typography
                  component="span"
                  color="var(--neutral-700)"
                  fontSize={12}
                  fontWeight={600}
                >
                  {selectedIds.length}
                </Typography>
              </Typography>
            }
            extraComponent={
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <FilterPopover />
                <GKButton
                  variant="text"
                  onClick={() => {
                    form.setValue(
                      "portfolioSelectionFilter.portfolio_group",
                      [],
                    );
                    form.setValue(
                      "portfolioSelectionFilter.portfolio_value",
                      [],
                    );
                  }}
                  sx={{
                    color: "var(--grey-700)",
                    fontSize: 13,
                    fontWeight: 500,
                    textDecoration: "underline",
                    textTransform: "none",
                    "&:hover": {
                      bgcolor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  Clear All
                </GKButton>
              </Box>
            }
          />
        );
      }}
    />
  );
};

export default PortfolioSelectionTable;
